import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-[0_8px_24px_hsl(0_50%_62%/0.3)]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-brand-surface hover:text-brand-text hover:border-brand-border hover:shadow-[0_0_16px_hsl(0_50%_70%/0.12)] dark:border-brand-border-subtle dark:hover:border-brand-border dark:hover:bg-brand-surface dark:hover:shadow-[0_0_12px_hsl(0_50%_70%/0.15)]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground hover:text-brand-text dark:hover:text-brand-text",
        link: "text-primary underline-offset-4 hover:underline hover:shadow-none",
        gold: "bg-[hsl(40,45%,57%)] text-white border border-[hsl(40,45%,57%)] hover:bg-white hover:text-[hsl(40,45%,57%)] btn-shine",
        outlineGold: "bg-white border border-[hsl(40,45%,57%)] text-[hsl(40,45%,44%)] hover:bg-[hsl(40,45%,57%)] hover:text-white btn-shine",
        outlineWhite: "bg-transparent border border-white text-white hover:bg-white hover:text-[#1A1A1A]",
        solidBlack: "bg-[#1A1A1A] text-white hover:bg-[#222]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
